// brand.jsx — inline SVG logos used across the funnel.
// Kept in one module so visual tweaks land in one place.

// ──────────────────────────────────────────────────────────────
// Opal — app icon (rounded-square bezel with gem)
// ──────────────────────────────────────────────────────────────
export function OpalAppIcon({ size = 64, radius = 0.22 }) {
  const r = size * radius;
  return (
    <img
      src="/assets/opal-app-icon.jpg"
      alt="Opal app icon"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: r,
        display: 'block',
        objectFit: 'cover',
      }}
    />
  );
}

// ──────────────────────────────────────────────────────────────
// Opal — the actual Opal gemstone (brand PNG).
// Served from /assets/opal-gem.png (public/assets/opal-gem.png).
// ──────────────────────────────────────────────────────────────
export function OpalGem({ size = 180, style = {} }) {
  return (
    <img
      src="/assets/opal-gem.png"
      alt="Opal gemstone"
      width={size}
      height={size}
      style={{
        width: size, height: size, objectFit: 'contain',
        display: 'block',
        ...style,
      }}
    />
  );
}

// ──────────────────────────────────────────────────────────────
// Press logos — inline SVG so no network fetch and color-adaptive.
// ──────────────────────────────────────────────────────────────
export function AppleLogo({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-label="Apple" role="img">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25C11.88 5.02 13.69 3.18 15.77 3c.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

export function ForbesWordmark({ height = 16, color = 'currentColor' }) {
  return (
    <span style={{
      fontFamily: '"Times New Roman", Georgia, serif',
      fontWeight: 600,
      fontSize: height,
      letterSpacing: '-0.02em',
      color,
      fontStyle: 'italic',
      lineHeight: 1,
    }}>Forbes</span>
  );
}

export function NYTWordmark({ height = 16, color = 'currentColor' }) {
  return (
    <span style={{
      fontFamily: '"Old English Text MT", "Cheltenham", Georgia, serif',
      fontWeight: 400,
      fontSize: height,
      color,
      letterSpacing: '0.01em',
      lineHeight: 1,
    }}>The New York Times</span>
  );
}

export function WiredWordmark({ height = 16, color = 'currentColor' }) {
  return (
    <span style={{
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 600,
      fontSize: height,
      letterSpacing: '0.02em',
      color,
      lineHeight: 1,
      textTransform: 'uppercase',
    }}>WIRED</span>
  );
}

export function TechCrunchWordmark({ height = 14, color = 'currentColor' }) {
  return (
    <span style={{
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 600,
      fontSize: height,
      color,
      letterSpacing: '-0.02em',
      lineHeight: 1,
    }}>TechCrunch</span>
  );
}

export function WSJWordmark({ height = 16, color = 'currentColor' }) {
  return (
    <span style={{
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontWeight: 600,
      fontSize: height,
      color,
      letterSpacing: '-0.03em',
      lineHeight: 1,
    }}>The Wall Street Journal</span>
  );
}

export function PressLogo({ name, size = 18 }) {
  const common = { color: 'rgba(255,255,255,0.9)' };
  switch (name) {
    case 'apple':     return <AppleLogo size={size} {...common} />;
    case 'forbes':    return <ForbesWordmark height={size} {...common} />;
    case 'nyt':       return <NYTWordmark height={size * 0.95} {...common} />;
    case 'wired':     return <WiredWordmark height={size} {...common} />;
    case 'tc':        return <TechCrunchWordmark height={size * 0.8} {...common} />;
    case 'wsj':       return <WSJWordmark height={size * 0.78} {...common} />;
    default:          return null;
  }
}

// ──────────────────────────────────────────────────────────────
// University logos — simplified seal/wordmark marks
// ──────────────────────────────────────────────────────────────
// Monochrome shield + wordmark so it reads on the dark card surface directly.
// Scales with `height`; `color` defaults to --fg1 when unspecified so the mark
// inherits the surrounding text color.
export function HarvardMark({ height = 28, color = 'currentColor' }) {
  return (
    <svg
      width={height * 4.6}
      height={height}
      viewBox="0 0 184 40"
      fill="none"
      aria-label="Harvard Business Review"
    >
      <g fill={color}>
        <path d="M20 4c5 0 9 1 12 2v12c0 8-6 14-12 18-6-4-12-10-12-18V6c3-1 7-2 12-2z"
          opacity="0.95" stroke={color} strokeWidth="0.6"/>
        <text x="20" y="24" textAnchor="middle"
          fontFamily="Georgia, 'Times New Roman', serif" fontWeight="600"
          fontSize="8" letterSpacing="0.5" fill="#050505">VERITAS</text>
      </g>
      <text x="44" y="18"
        fontFamily="Georgia, 'Times New Roman', serif" fontWeight="600"
        fontSize="13" letterSpacing="-0.01em" fill={color}>Harvard</text>
      <text x="44" y="32"
        fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif" fontWeight="500"
        fontSize="9" letterSpacing="0.16em" textTransform="uppercase"
        fill={color} opacity="0.7">BUSINESS REVIEW</text>
    </svg>
  );
}

export function MITMark({ size = 40 }) {
  return (
    <svg width={size} height={size * 0.56} viewBox="0 0 72 40" aria-label="MIT">
      <g fill="#8a1538">
        <rect x="0" y="8" width="8" height="24"/>
        <rect x="12" y="8" width="8" height="24"/>
        <rect x="24" y="8" width="8" height="24"/>
        <rect x="36" y="8" width="8" height="24"/>
        <rect x="36" y="0" width="16" height="8"/>
        <rect x="56" y="8" width="8" height="24"/>
        <rect x="52" y="0" width="16" height="8"/>
      </g>
    </svg>
  );
}

export function StanfordMark({ size = 40 }) {
  return (
    <svg width={size * 1.8} height={size * 0.5} viewBox="0 0 140 40" aria-label="Stanford">
      <text x="0" y="28"
        fill="#8c1515" fontFamily="Georgia, 'Times New Roman', serif"
        fontWeight="600" fontSize="24" letterSpacing="0.02em">Stanford</text>
    </svg>
  );
}

export function UCLAMark({ size = 40 }) {
  return (
    <svg width={size * 1.6} height={size * 0.6} viewBox="0 0 100 38" aria-label="UCLA">
      <text x="0" y="30"
        fill="#2774ae" fontFamily="'Helvetica Neue', Helvetica, sans-serif"
        fontWeight="600" fontSize="32" letterSpacing="-0.02em">UCLA</text>
    </svg>
  );
}

export function UCIrvineMark({ height = 28, color = 'currentColor' }) {
  return (
    <svg
      width={height * 3.8}
      height={height}
      viewBox="0 0 152 40"
      fill="none"
      aria-label="UC Irvine"
    >
      <g fill={color}>
        <circle cx="20" cy="20" r="14" opacity="0.12"/>
        <circle cx="20" cy="20" r="14" fill="none" stroke={color} strokeWidth="1.2"/>
        <text x="20" y="24" textAnchor="middle"
          fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
          fontWeight="600" fontSize="12" letterSpacing="-0.01em">UCI</text>
      </g>
      <text x="44" y="18"
        fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif" fontWeight="600"
        fontSize="14" letterSpacing="-0.01em" fill={color}>UC Irvine</text>
      <text x="44" y="32"
        fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif" fontWeight="500"
        fontSize="8" letterSpacing="0.14em" textTransform="uppercase"
        fill={color} opacity="0.7">UNIVERSITY OF CALIFORNIA</text>
    </svg>
  );
}

export function OxfordMark({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="Oxford">
      <defs>
        <linearGradient id="uo-shield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#10347a"/>
          <stop offset="1" stopColor="#0b2456"/>
        </linearGradient>
      </defs>
      <path d="M32 4c9 0 16 2 22 4v22c0 14-12 24-22 30C22 54 10 44 10 30V8c6-2 13-4 22-4z"
        fill="url(#uo-shield)" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
      <g fill="#fff" fontFamily="'Times New Roman', serif" fontWeight="600" fontSize="18" textAnchor="middle">
        <text x="32" y="40">OU</text>
      </g>
    </svg>
  );
}

export function UniversityLogo({ name, height = 28, color = 'currentColor' }) {
  switch (name) {
    case 'harvard':  return <HarvardMark height={height} color={color} />;
    case 'mit':      return <MITMark size={height} />;
    case 'stanford': return <StanfordMark size={height} />;
    case 'ucla':     return <UCLAMark size={height} />;
    case 'uci':      return <UCIrvineMark height={height} color={color} />;
    case 'oxford':   return <OxfordMark size={height} />;
    default:         return null;
  }
}

// ──────────────────────────────────────────────────────────────
// Social-app icons — real App Store artwork (fetched via iTunes Lookup API
// from public/assets/app-icons/*.jpg). Treat these as trademarks of their
// respective owners; swap to officially-licensed variants if required.
// ──────────────────────────────────────────────────────────────
// Opaque slugs (not brand names) so first-party privacy filters that
// substring-match URL paths against social-tracker blocklists let these
// through. The actual file names mirror the slug.
const APP_ICON_SLUG = {
  Instagram:   'app-ig',
  TikTok:      'app-tt',
  YouTube:     'app-yt',
  'Twitter/X': 'app-x',
  X:           'app-x',
  Reddit:      'app-rd',
  Snapchat:    'app-sc',
  Facebook:    'app-fb',
  Netflix:     'app-nf',
  Messages:    'app-msg',
  Discord:     'app-dc',
  Pinterest:   'app-pi',
  LinkedIn:    'app-li',
  Gmail:       'app-gm',
};

// Some artwork is the macOS variant, whose rounded squircle sits inside a
// padded canvas (white corners) rather than bleeding to the edges like the
// iOS icons. Scaling those up inside an overflow-hidden frame crops the white
// margin away so they match the full-bleed icons.
const APP_ICON_CROP = {
  Messages: 1.2,
};

export function AppIcon({ name, size = 40 }) {
  const slug = APP_ICON_SLUG[name];
  if (!slug) return null;
  const crop = APP_ICON_CROP[name];
  return (
    <div
      style={{
        width: size, height: size,
        borderRadius: size * 0.23,
        overflow: 'hidden',
        display: 'block', flexShrink: 0,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(255,255,255,0.04)',
      }}
    >
      <img
        src={`/assets/app-icons/${slug}.jpg`}
        alt={name}
        width={size}
        height={size}
        style={{
          width: size, height: size,
          display: 'block',
          transform: crop ? `scale(${crop})` : undefined,
        }}
      />
    </div>
  );
}

export function AppIconFor(name) {
  return <AppIcon name={name} />;
}

// ──────────────────────────────────────────────────────────────
// Review avatars — PLACEHOLDER initials-on-color tiles.
// TODO: replace `src` in Avatar with real customer portrait URLs.
// ──────────────────────────────────────────────────────────────
const AVATAR_TINTS = [
  { bg: 'linear-gradient(135deg,#8a65ff,#d488ff)', fg: '#fff' },
  { bg: 'linear-gradient(135deg,#ff9f6a,#ff5775)', fg: '#fff' },
  { bg: 'linear-gradient(135deg,#54e0c7,#2f8eff)', fg: '#fff' },
  { bg: 'linear-gradient(135deg,#ffd26a,#ff8a5c)', fg: '#2a1000' },
  { bg: 'linear-gradient(135deg,#7ad3ff,#6a7cff)', fg: '#fff' },
  { bg: 'linear-gradient(135deg,#b4e37a,#54c39a)', fg: '#0f2a10' },
];
function hashSeed(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return Math.abs(h);
}
export function Avatar({ name, size = 40, src, style = {} }) {
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const tint = AVATAR_TINTS[hashSeed(name || '') % AVATAR_TINTS.length];
  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        width={size}
        height={size}
        style={{
          width: size, height: size, borderRadius: 9999,
          objectFit: 'cover', flexShrink: 0,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
          ...style,
        }}
      />
    );
  }
  return (
    <div
      aria-label={name}
      style={{
        width: size, height: size, borderRadius: 9999,
        background: tint.bg,
        color: tint.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-display)', fontWeight: 600,
        fontSize: size * 0.38,
        letterSpacing: '-0.01em',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
        flexShrink: 0,
        ...style,
      }}
    >{initials}</div>
  );
}

// ──────────────────────────────────────────────────────────────
// Opal wordmark — the real "Opal" logotype from
// assets/Opal Logo Black.svg. Switchable to color via `color`.
// ──────────────────────────────────────────────────────────────
export function OpalWordmarkLogo({ height = 22, color = '#fff' }) {
  const aspect = 283 / 133.654;
  return (
    <svg
      width={height * aspect}
      height={height}
      viewBox="0 0 283 134"
      fill="none"
      aria-label="Opal"
    >
      <g clipPath="url(#olb-clip)">
        <path d="M159.73 39.4521C168.829 39.4521 176.539 43.1065 181.947 49.5254C187.335 55.9203 190.35 64.9469 190.35 75.5938C190.35 86.2382 187.353 95.2627 182.009 101.658C176.644 108.079 168.999 111.74 159.991 111.74C151.703 111.74 144.67 108.749 139.885 103.298V131.974C139.885 132.901 139.133 133.654 138.205 133.654H126.892C125.964 133.654 125.212 132.901 125.212 131.974V41.7754C125.212 40.8479 125.964 40.0959 126.892 40.0957H137.62C138.548 40.0957 139.301 40.8478 139.301 41.7754V47.9854C144.154 42.6735 151.697 39.4522 159.73 39.4521ZM57.2402 0C88.8542 0 114.482 25.6283 114.482 57.2422C114.482 88.8561 88.8541 114.482 57.2402 114.482C25.6264 114.482 5.12151e-05 88.856 0 57.2422C0 25.6283 25.6264 9.5512e-05 57.2402 0ZM228.387 39.4424C236.458 39.4424 243.312 41.6892 248.177 45.8154C253.068 49.9643 255.817 55.9058 255.817 62.9717V109.408C255.817 110.336 255.065 111.088 254.138 111.088H243.407C242.479 111.088 241.728 110.336 241.728 109.408V102.892C236.857 108.71 229.319 112.323 221.428 112.323C214.359 112.323 208.307 110.168 204.002 106.271C199.682 102.362 197.246 96.808 197.246 90.2881C197.246 84.0186 199.684 78.8438 204.281 75.1592C208.823 71.5192 215.332 69.4402 223.346 68.9727H223.348L241.143 67.9541V63.9482C241.143 60.2335 239.948 57.5136 237.885 55.6992C235.79 53.8572 232.577 52.7529 228.189 52.7529C220.366 52.7531 215.447 56.3544 214.358 61.6836C214.199 62.4656 213.51 63.0273 212.712 63.0273H201.918C201.464 63.0272 201.03 62.8437 200.714 62.5186C200.398 62.1934 200.227 61.7541 200.239 61.3008C200.599 48.4025 212.979 39.4425 228.387 39.4424ZM281.321 13.8877C282.249 13.8878 283.001 14.6399 283.001 15.5674V109.406C283.001 110.334 282.249 111.087 281.321 111.087H270.005C269.077 111.087 268.325 110.334 268.325 109.406V15.5674C268.326 14.6399 269.077 13.8878 270.005 13.8877H281.321ZM224.851 81.1055C220.489 81.3859 217.275 82.4528 215.192 84.0283C213.175 85.5541 212.117 87.6257 212.117 90.2881C212.117 92.9911 213.229 95.1719 215.19 96.7158C217.188 98.2881 220.173 99.2744 223.963 99.2744C233.846 99.2744 241.142 92.4077 241.143 83.7217V80.1094L224.851 81.1055ZM75.1543 17.7441C66.3289 14.8113 50.4144 15.6929 50.3193 15.6982C48.3437 15.8528 46.361 16.7211 44.4795 17.7441C42.5979 18.7672 37.459 22.6059 33.7832 27.7852C31.1389 31.1753 25.5156 39.4289 20.0742 47.4844C15.283 54.5822 13.4346 58.2937 13.4346 65.0723C13.4346 70.7117 16.079 74.4733 18.9062 77.0957C21.7336 79.7181 41.1592 94.9374 44.9092 97.3047C48.6588 99.6717 54.3757 99.2935 59.7852 97.0508C65.1947 94.808 81.7316 86.944 89.1084 82.8516C96.4846 78.7594 97.8236 77.3784 99.9268 73.2578C101.494 70.1969 101.34 67.3559 99.9268 63.0166C98.5139 58.6772 92.9817 46.5911 91.2598 42.6885C89.5378 38.7859 85.3596 29.9732 83.8828 26.9541C82.4061 23.9352 81.2021 21.1741 79.334 19.6621C77.5985 18.2557 75.1819 17.7499 75.1543 17.7441ZM157.52 52.8193C152.295 52.8194 147.929 55.0594 144.831 58.9707C141.71 62.9115 139.821 68.6314 139.821 75.5918C139.821 82.5864 141.711 88.3051 144.83 92.2363C147.926 96.1376 152.291 98.3642 157.52 98.3643C162.905 98.3643 167.314 96.1822 170.412 92.3262C173.537 88.4364 175.415 82.726 175.415 75.5918C175.415 68.492 173.538 62.7801 170.411 58.8809C167.311 55.0151 162.901 52.8193 157.52 52.8193Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="olb-clip">
          <rect width="283" height="133.653" fill={color}/>
        </clipPath>
      </defs>
    </svg>
  );
}
